import React from 'react';
import { Stack, Theme, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { PRO_PITCH_WIDTH, ProPitchSheet } from 'src/components/careerProPitch';
import useAppNavigate from 'src/helpers/hooks/useAppNavigate';
import { canUseProDesigns } from 'src/helpers/resumeBuilder';
import { refreshUserCredits } from 'src/redux/actions/auth';
import { getApplicantStatusInfo } from 'src/redux/actions/candidateLayout';
import { UserRole } from 'src/constants';
import { useBuyAiCreditsMutation } from 'src/redux/services/aiCredits';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { closeAddAiCredits } from 'src/redux/slices/appState';
import { logBuyDialogShown } from 'src/helpers/paymentFunnel';
import BuyStep from './BuyStep';
import DialogHeader from './DialogHeader';
import SuccessStep from './SuccessStep';
import { Dialog, Drawer } from './styles/index.styles';
import { PurchaseResult } from './types';

/**
 * A free candidate who runs out is pitched Pro first, in this same sheet -- led
 * by the need closest to what they were doing -- with the packs one tap away.
 * Pro members, a voluntary open and a caller that already pitched Pro land on
 * the packs.
 *
 * Spend Credits from the vault to top up AI Credits. Mounted once at the app
 * root and opened by dispatching `openAddAiCredits()`, so any feature that hits
 * a `not_enough_ai_credits` wall can offer the fix without wiring its own copy.
 *
 * Sells the same pack as the Chrome extension, so a credit bought here is
 * indistinguishable from one bought there.
 */
const AddAiCreditsDialog = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const isTabView = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const open = useAppSelector((state) => state.appState.isAddAiCreditsOpen);
  const trigger = useAppSelector((state) => state.appState.addAiCreditsTrigger);
  const need = useAppSelector((state) => state.appState.addAiCreditsNeed);
  const blocked = useAppSelector((state) => state.appState.addAiCreditsAction);
  const topUpOnly = useAppSelector((state) => state.appState.addAiCreditsTopUp);
  const reason = useAppSelector(
    (state) => state.appState.addAiCreditsReason ?? undefined,
  );
  const aiCredits = useAppSelector(
    (state) =>
      state.candidate.applicantInfoData?.remaining_ai_interactions ?? 0,
  );
  const { user } = useAppSelector((state) => state.auth);
  const { constants } = useAppSelector((state) => state.appState);
  const [buyAiCredits, { isLoading }] = useBuyAiCreditsMutation();

  const [qty, setQty] = React.useState(1);
  const [result, setResult] = React.useState<PurchaseResult | null>(null);
  // Chose the packs from the Pro pitch.
  const [packs, setPacks] = React.useState(false);
  // Ambassadors' Pro counts too: buying the pass would replace it.
  const isPro = canUseProDesigns(user ?? undefined);
  const pitch = !isPro && !topUpOnly && !packs && !result;

  // Opened on a refusal, the balance on screen was the wrong one: re-read it,
  // so the counter here and the next gate both work from the server's.
  React.useEffect(() => {
    if (open && user?.user_type === UserRole.Applicant) {
      dispatch(getApplicantStatusInfo());
    }
  }, [open, user?.user_type, dispatch]);

  // Logged as it opens, with what it opens on; the packs log again if the
  // pitch's "Top up" brings them up. `pitch` is read on opening only: it
  // changes later (a purchase, the top-up) without the dialog reopening.
  React.useEffect(() => {
    if (open) {
      logBuyDialogShown(
        'ai_credits',
        trigger,
        pitch ? 'pro_pitch' : 'ai_packs',
        reason,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trigger, reason]);

  const handleClose = () => {
    dispatch(closeAddAiCredits());
    // Reset after the close transition so the body doesn't visibly swap back to
    // the buy step on the way out.
    setTimeout(() => {
      setResult(null);
      setQty(1);
      setPacks(false);
    }, 220);
  };

  // Mounted at the app root, so it outlives the page that opened it. Pro bought
  // from the pitch's checkout lands on its success page, where this would
  // otherwise reappear as the AI-credit packs over it.
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (open) handleClose();
    // Only a navigation closes it, never `open` itself changing.
  }, [pathname]);

  const packSize = constants?.aiCredits?.packSize ?? 100;
  const packCost = constants?.aiCredits?.creditsCost ?? 25;

  const handleBuy = async () => {
    try {
      const data = await buyAiCredits({ quantity: qty }).unwrap();
      setResult({
        added: data.answers_added ?? qty * packSize,
        charged: data.credits_charged ?? qty * packCost,
        aiCredits: data.ai_answers ?? 0,
        credits: data.credits ?? 0,
      });
      // Only the credits half needs a nudge — the AI Credits balance arrives on
      // its own over AICreditChange.
      dispatch(refreshUserCredits());
    } catch (err: any) {
      enqueueSnackbar(
        err?.error ?? "Couldn't add AI Credits. Please try again.",
        { variant: 'error' },
      );
    }
  };

  const content = pitch ? (
    <Stack sx={{ maxHeight: { xs: '92vh', md: '86vh' } }}>
      <ProPitchSheet
        placement="ai_credits_wall"
        reason={reason}
        lead={need ?? undefined}
        shortfall={{ balance: aiCredits, ...blocked }}
        links={[
          {
            label: 'Just need AI credits for this one? Top up →',
            onClick: () => {
              setPacks(true);
              logBuyDialogShown('ai_credits', trigger, 'ai_packs', reason);
            },
            cta: 'pro_pitch_top_up',
          },
        ]}
        onClose={handleClose}
      />
    </Stack>
  ) : (
    <>
      <DialogHeader done={!!result} onClose={handleClose} />
      {result ? (
        <SuccessStep result={result} mobile={isTabView} onClose={handleClose} />
      ) : (
        <BuyStep
          packSize={packSize}
          packCost={packCost}
          maxPacks={constants?.aiCredits?.maxPacks ?? 10}
          qty={qty}
          setQty={setQty}
          credits={user?.credits ?? 0}
          isLoading={isLoading}
          mobile={isTabView}
          onBuy={handleBuy}
          onGetCredits={() => {
            handleClose();
            navigate('/store');
          }}
        />
      )}
    </>
  );

  return isTabView ? (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="bottom"
      sx={{
        '& .MuiDrawer-paper': {
          p: '0px',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={
        pitch
          ? { '& .MuiDialog-paper': { maxWidth: `${PRO_PITCH_WIDTH}px` } }
          : undefined
      }
    >
      {content}
    </Dialog>
  );
};

export default AddAiCreditsDialog;
